import React from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useTheme } from "../../contexts/ThemeContext";
import { Theme } from "../../theme";

// アニメーション
const fadeInScale = keyframes`
	from {
		opacity: 0;
		transform: scale(0.95);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
`;

// Styled Components - 参照される順に定義
const CardIcon = styled.div<{ theme: Theme }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.5rem;
	height: 2.5rem;
	background: ${props => props.theme.colors.background.tertiary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: 0.75rem;
	color: ${props => props.theme.colors.primary.main};
	font-size: 1.125rem;
	transition: background-color 0.2s ease, color 0.2s ease;
`;

const CardHeader = styled.div<{ theme: Theme }>`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1rem;
	padding-bottom: 0.75rem;
	border-bottom: 1px solid ${props => props.theme.colors.neutral[200]};
`;

const CardTitle = styled.h3<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.primary};
	font-size: ${props => props.theme.typography.roles.title.fontSize};
	font-weight: ${props => props.theme.typography.roles.title.fontWeight};
	margin: 0;

	@media (max-width: 768px) {
		font-size: 1.25rem;
	}
`;

const CardBody = styled.div`
	flex-grow: 1;
`;

const CardContainer = styled.div<{ theme: Theme }>`
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: ${props => props.theme.density.cardRadius};
	padding: ${props => props.theme.density.cardPadding};
	transition: border-color 0.2s ease, transform 0.2s ease;
	animation: ${fadeInScale} 0.3s ease-out;
	height: 100%;
	display: flex;
	flex-direction: column;
	box-shadow: ${props => props.theme.shadows.base};

	&:hover {
		transform: translateY(-2px);
		border-color: ${props => props.theme.colors.primary.main};

		${CardIcon} {
			background: ${props => props.theme.colors.primary.main};
			color: #ffffff;
		}
	}

	@media (max-width: 768px) {
		padding: ${props => props.theme.density.cardPaddingMobile};
	}
`;

interface CardProps {
	icon: IconDefinition;
	title: string;
	body: React.ReactNode;
}

const Card: React.FC<CardProps> = (props) => {
	const { icon, title, body } = props;
	const { theme } = useTheme();

	return (
		<CardContainer theme={theme}>
			<CardHeader theme={theme}>
				<CardIcon theme={theme}>
					<FontAwesomeIcon icon={icon} />
				</CardIcon>
				<CardTitle theme={theme}>{title}</CardTitle>
			</CardHeader>
			<CardBody>
				{body}
			</CardBody>
		</CardContainer>
	);
};

export default Card;
